// Recent orders — last 5 from the customer's history; order numbers open the order pop-up.
import { useEffect, useState } from "react";
import { ExternalLink, ShoppingBag } from "lucide-react";
import api from "../../../lib/api";
import { GBP } from "../_quoteHelpers";
import { T2Panel } from "../_t2";
import { OrderDetailModal } from "../../orders/OrdersDashboard";

const LIMIT = 5;

const money = (v) => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const ts = (d) => { const t = Date.parse(d || ""); return Number.isFinite(t) ? t : 0; };

// Person's linked orders (same source as Customer 360) merged with billing-email matches; newest first.
async function loadOrders(customer) {
  const byId = new Map();
  if (customer.personId) {
    try {
      const { data } = await api.get(`/crm/persons/${customer.personId}?include_archived=false`);
      for (const o of data.orders || []) byId.set(o.id, { id: o.id, num: o.bc_order_id, date: o.date_created, status: o.status, total: money(o.total_inc_tax) });
      const p = data.person || {};
      if (p.ltv != null || p.orders_count != null) byId.stats = { ltv: p.ltv, orders: p.orders_count };
    } catch { /* email fallback below */ }
  }
  if (customer.email) {
    try {
      const p = new URLSearchParams({ page: 1, limit: 50, search: customer.email.trim() });
      const { data } = await api.get(`/lab/orders?${p.toString()}`);
      for (const o of data.items || []) {
        if (byId.has(o._id)) continue;
        const bc = o.BCOrder || {};
        byId.set(o._id, { id: o._id, num: bc.id ?? bc._id, date: bc.date_created, status: bc.status, total: money(bc.total_inc_tax ?? bc.total_ex_tax) });
      }
    } catch { /* keep person orders */ }
  }
  const out = [...byId.values()].sort((a, b) => ts(b.date) - ts(a.date));
  out.stats = byId.stats || null; // Customer 360 figures (source of truth for LTV / order count)
  return out;
}

function Stat({ label, value, testid }) {
  return (
    <div style={{ padding: "7px 8px", borderRadius: 7, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", minWidth: 0 }} data-testid={testid}>
      <div className="t2-mono" style={{ fontSize: 13, fontWeight: 800, color: "var(--t2-white)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      <div className="t2-mono" style={{ fontSize: 8.5, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t2-grey3)" }}>{label}</div>
    </div>
  );
}

export default function RecentOrdersPanel({ customer, embedded = false }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const key = `${customer?.personId || ""}|${customer?.email || ""}`;

  useEffect(() => {
    if (!customer?.email && !customer?.personId) { setAll([]); return; }
    let alive = true;
    setLoading(true);
    loadOrders(customer).then((r) => alive && setAll(r)).catch(() => alive && setAll([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = all.slice(0, LIMIT);
  const has = customer?.email || customer?.personId;
  const list = (
    <>
      {!has && <div style={{ fontSize: 11, color: "var(--t2-grey3)" }}>Pick a customer to see order history.</div>}
      {has && loading && <div style={{ fontSize: 11, color: "var(--t2-grey3)" }}>Loading…</div>}
      {has && !loading && rows.length === 0 && (
        <div style={{ fontSize: 11, color: "var(--t2-grey3)" }} data-testid="recent-orders-empty">No previous orders found for this customer.</div>
      )}
      {!loading && rows.map((o) => (
        <div key={o.id} className="t2-ord-row" data-testid={`recent-order-${o.id}`}>
          <button type="button" onClick={() => setOpenId(o.id)} className="qb-order-link" data-testid={`recent-order-link-${o.id}`}
            title="Open order details">
            #{o.num ?? String(o.id).slice(-6)} <ExternalLink className="w-3 h-3" />
          </button>
          <span style={{ minWidth: 0 }}>
            <b>{o.date ? new Date(o.date).toLocaleDateString("en-GB") : "—"}</b>
            {o.status && <span style={{ display: "block", fontSize: 10, color: "var(--t2-grey3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.status}</span>}
          </span>
          <span><b>{o.total != null ? GBP(o.total) : "—"}</b></span>
        </div>
      ))}
      {openId && <OrderDetailModal orderId={openId} onClose={() => setOpenId(null)} />}
    </>
  );

  if (embedded) {
    // Same figures as Customer 360; fall back to the loaded order history.
    const s360 = all.stats || {};
    const sum = all.reduce((s, o) => s + (o.total || 0), 0);
    const count = s360.orders ?? (customer?.orders || null) ?? (all.length || null);
    const ltv = s360.ltv ?? (customer?.ltv || null) ?? (all.length ? sum : null);
    const avg = ltv != null && count ? ltv / count : (customer?.avgOrder ?? null);
    const g0 = (v) => "£" + Math.round(v).toLocaleString("en-GB");
    return (
      <div style={{ marginTop: 12 }} data-testid="panel-recent">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
          <Stat label={customer?.isLead ? "Lead" : "LTV"} value={customer?.isLead ? "£0" : (ltv != null ? g0(ltv) : "—")} testid="customer-stat-ltv" />
          <Stat label="Orders" value={count ?? "—"} testid="customer-stat-orders" />
          <Stat label="Avg" value={avg != null ? g0(avg) : "—"} testid="customer-stat-avg" />
        </div>
        {(loading || rows.length > 0) && (
          <div style={{ marginTop: 10 }}>
            <div className="t2-mono" style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--t2-grey3)", marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
              <span>RECENT ORDERS</span><span data-testid="recent-orders-count">{all.length || ""}</span>
            </div>
            {list}
          </div>
        )}
      </div>
    );
  }

  return (
    <T2Panel icon={ShoppingBag} title="Recent orders" testid="panel-recent" right={<span data-testid="recent-orders-count">{rows.length ? `${rows.length}` : "—"}</span>}>
      {list}
    </T2Panel>
  );
}
